import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge } from "../components/Shared";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, ShoppingCart, Users, Truck, PackageSearch, Clock, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const Card = ({ label, value, icon: Icon, testid, accent, to }) => {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <div className="label-caps">{label}</div>
        <Icon className={`w-4 h-4 ${accent || "text-slate-400"}`} />
      </div>
      <div className="mt-4 font-display text-4xl tracking-tighter text-slate-900">{value}</div>
    </>
  );
  const cls = "block bg-white border border-slate-300 p-6 hover:border-slate-500 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow,border-color] duration-200";
  if (to) {
    return (
      <Link to={to} className={cls} data-testid={testid} title={`Go to ${label}`}>
        {inner}
      </Link>
    );
  }
  return (
    <div className={cls} data-testid={testid}>
      {inner}
    </div>
  );
};

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const downloadBackup = async () => {
    try {
      const res = await api.get("/backup/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `icmarkt-backup-${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch { toast.error("Failed to download backup"); }
  };

  const downloadBackupJson = async () => {
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `icmarkt-backup-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Raw JSON backup downloaded");
    } catch { toast.error("Failed to download JSON backup"); }
  };

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Overview"
        testid="dashboard-title"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={downloadBackup}
              className="rounded-none border-slate-300 hover:bg-slate-100"
              data-testid="backup-btn"
              title="Download Excel backup"
            >
              <Download className="w-4 h-4 mr-2" /> Backup (Excel)
            </Button>
            <button
              type="button"
              onClick={downloadBackupJson}
              className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
              data-testid="backup-json-btn"
              title="Download raw JSON snapshot"
            >
              Raw JSON
            </button>
          </div>
        }
      />
      <PageBody>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Card label="Open Offers" value={s?.open_offers ?? "—"} icon={FileText} testid="stat-open-offers" accent="text-blue-600" to="/offers" />
          <Card label="Active Orders" value={s?.active_orders ?? "—"} icon={ShoppingCart} testid="stat-active-orders" accent="text-emerald-600" to="/orders" />
          <Card label="Pending Items" value={s?.pending_purchase_items ?? "—"} icon={PackageSearch} testid="stat-pending-items" accent="text-orange-500" to="/order-items" />
          <Card label="Customers" value={s?.customers ?? "—"} icon={Users} testid="stat-customers" to="/customers" />
          <Card label="Suppliers" value={s?.suppliers ?? "—"} icon={Truck} testid="stat-suppliers" to="/suppliers" />
          <Card label="Total Offers" value={s?.total_offers ?? "—"} icon={FileText} testid="stat-total-offers" to="/offers" />
          <Card label="Total Orders" value={s?.total_orders ?? "—"} icon={ShoppingCart} testid="stat-total-orders" to="/orders" />
          <Card label="Session" value="LIVE" icon={Clock} testid="stat-session" />
        </div>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-caps">Latest</div>
              <h2 className="font-display text-2xl tracking-tight text-slate-900">Recent Orders</h2>
            </div>
            <Link to="/orders" className="text-sm text-blue-700 hover:text-blue-900 inline-flex items-center gap-1" data-testid="link-view-all-orders">
              View all <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-white border border-slate-300">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300">
                <tr className="text-left">
                  <th className="py-2 px-3 label-caps">Order #</th>
                  <th className="py-2 px-3 label-caps">Customer</th>
                  <th className="py-2 px-3 label-caps">Items</th>
                  <th className="py-2 px-3 label-caps">Status</th>
                  <th className="py-2 px-3 label-caps">Created</th>
                </tr>
              </thead>
              <tbody>
                {(s?.recent_orders || []).map((o) => (
                  <tr key={o.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="py-2 px-3 font-mono">
                      <Link to={`/orders/${o.id}`} className="text-blue-700 hover:text-blue-900">{o.order_number}</Link>
                    </td>
                    <td className="py-2 px-3">{o.customer_name}</td>
                    <td className="py-2 px-3 font-mono">{o.item_count}</td>
                    <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                    <td className="py-2 px-3 font-mono text-slate-500">{o.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
                {!(s?.recent_orders || []).length && (
                  <tr><td colSpan="5" className="py-10 text-center text-slate-500">No orders yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </PageBody>
    </>
  );
}
